/**
 * Eine Notiz schreiben: ein Feld, keine Pflichtangaben. Zeilen, die mit „-"
 * beginnen, werden Punkte zum Abhaken — so lässt sich eine Besichtigung in
 * einem Rutsch festhalten (auch per Diktat auf dem Telefon).
 *
 * `bezug` steht fest, wenn die Notiz am Kunden, Projekt, Termin oder Leed
 * entsteht; ohne ihn (Aufgabenseite) wählt man ihn im Suchfeld. `eingeklappt`
 * zeigt zuerst nur eine Zeile — der Rest erscheint, sobald man hineintippt.
 */
import { useState } from "react";
import { fehlerText } from "@vimaana/ui";

import { useNotizAnlegen, usePersonen } from "../../hooks/useNotizen";
import { vorname, zerlegen, type NotizBezugTeil } from "../../lib/notizen";
import BezugWahl from "./BezugWahl";
import "../../styles/projekte.css";
import "../../styles/notizen.css";

type Props = {
  bezug?: NotizBezugTeil;
  onFertig?: () => void;
  autoFocus?: boolean;
  platzhalter?: string;
  eingeklappt?: boolean;
};

export default function NotizErfassen({ bezug, onFertig, autoFocus, platzhalter, eingeklappt = false }: Props) {
  const anlegen = useNotizAnlegen();
  const personen = usePersonen().data || [];
  const ich = personen.find((p) => p.ich)?.id;
  const [offen, setOffen] = useState(!eingeklappt);
  const [roh, setRoh] = useState("");
  const [gewaehlt, setGewaehlt] = useState<NotizBezugTeil>({});
  const [fuer, setFuer] = useState<number | null>(null);
  const teile = zerlegen(roh);
  const leer = !teile.text && teile.punkte.length === 0;

  const zuruecksetzen = () => {
    setRoh(""); setGewaehlt({}); setFuer(null);
    if (eingeklappt) setOffen(false);
  };
  const speichern = () => {
    if (leer || anlegen.isPending) return;
    anlegen.mutate(
      { text: teile.text, punkte: teile.punkte, bezug: bezug ?? gewaehlt, zustaendig_id: fuer ?? undefined },
      { onSuccess: () => { zuruecksetzen(); onFertig?.(); } },
    );
  };

  if (!offen) {
    return (
      <input
        className="nz-feld nz-einzeile"
        placeholder={platzhalter || "Neue Aufgabe oder Notiz …"}
        aria-label="Neue Aufgabe oder Notiz"
        onFocus={() => setOffen(true)}
        onChange={(e) => { setRoh(e.target.value); setOffen(true); }}
        value=""
      />
    );
  }

  return (
    <div className="nz-erfassen">
      <textarea
        className="nz-feld nz-textfeld"
        rows={3}
        value={roh}
        onChange={(e) => setRoh(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) speichern();
          if (e.key === "Escape" && eingeklappt && !roh.trim()) zuruecksetzen();
        }}
        placeholder={platzhalter || "Was ist zu tun?\n- Zeilen mit Strich werden Punkte zum Abhaken"}
        aria-label="Notiz"
        autoFocus={autoFocus || eingeklappt}
      />
      {!bezug && <BezugWahl wert={gewaehlt} onWert={setGewaehlt} />}
      <div className="nz-zeile">
        {personen.length > 1 && (
          <div className="nz-fuer" role="group" aria-label="Wer erledigt es?">
            {personen.map((p) => {
              const aktiv = (fuer ?? ich) === p.id;
              return (
                <button key={p.id} type="button" className={"nz-seg" + (aktiv ? " is-aktiv" : "")} aria-pressed={aktiv} onClick={() => setFuer(p.id)}>
                  {p.ich ? "Für mich" : `Für ${vorname(p.name)}`}
                </button>
              );
            })}
          </div>
        )}
        <span className="nz-zeile-rechts">
          {(onFertig || eingeklappt) && (
            <button type="button" className="vg-btn vg-btn-ghost" onClick={() => { zuruecksetzen(); onFertig?.(); }}>Abbrechen</button>
          )}
          <button type="button" className="vg-btn vg-btn-primary" disabled={leer || anlegen.isPending} onClick={speichern}>
            {anlegen.isPending ? "Speichert …" : "Speichern"}
          </button>
        </span>
      </div>
      {anlegen.error && <p className="nz-fehler" role="alert">{fehlerText(anlegen.error)}</p>}
    </div>
  );
}
